import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { createTheme } from '@mui/material';
import { loadAppConfig, saveAppConfig } from '../services/configService';

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeContextProvider');
  }
  return context;
};

const defaultColors = {
  // Top Bar (Barra Superiore)
  topBarBackground: '#bf0404',
  topBarText: '#ffffff',
  // Body (Corpo)
  bodyBackground: '#323130',
  bodyPaper: '#3a3938',
  bodyPrimaryButton: '#bf0404',
  bodyPrimaryButtonHover: '#9a0303',
  bodyPrimaryButtonText: '#ffffff',
  // Text Colors (Colori Testo)
  textPrimary: '#ffffff',
  textSecondary: '#b3b3b3',
  // Footer (Footer inferiore)
  footerBackground: '#3a3938',
  footerText: '#b3b3b3', // Approximation of rgba(255, 255, 255, 0.7)
};

export const ThemeContextProvider = ({ children }) => {
  const [themeColors, setThemeColors] = useState(defaultColors);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load theme colors from API on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const config = await loadAppConfig();
        if (config && config.theme) {
          // Merge with defaults to ensure all properties exist
          const mergedColors = { ...defaultColors, ...config.theme };
          setThemeColors(mergedColors);
        }
      } catch (error) {
        console.error('Failed to load theme from API:', error);
        // Continue with default colors
      }
      setIsLoaded(true);
    };
    loadTheme();
  }, []);

  // Create theme based on current colors
  const theme = useMemo(() => {
    return createTheme({
      palette: {
        mode: 'dark',
        primary: {
          main: themeColors.bodyPrimaryButton,
          dark: themeColors.bodyPrimaryButtonHover,
          contrastText: themeColors.bodyPrimaryButtonText,
        },
        secondary: {
          main: '#dc004e',
        },
        background: {
          default: themeColors.bodyBackground,
          paper: themeColors.bodyPaper,
        },
        text: {
          primary: themeColors.textPrimary,
          secondary: themeColors.textSecondary,
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      },
      shape: {
        borderRadius: 8,
      },
      // Custom theme properties for specific sections
      custom: {
        topBar: {
          background: themeColors.topBarBackground,
          text: themeColors.topBarText,
        },
        footer: {
          background: themeColors.footerBackground,
          text: themeColors.footerText,
        },
      },
    });
  }, [themeColors]);

  // Function to update theme colors
  const updateThemeColors = async (newColors) => {
    setThemeColors(newColors);
    
    // Save to API
    try {
      const config = await loadAppConfig();
      const updatedConfig = {
        ...config,
        theme: newColors,
      };
      await saveAppConfig(updatedConfig);
    } catch (error) {
      console.error('Failed to save theme colors to API:', error);
    }
  };

  // Function to reset theme colors
  const resetThemeColors = async () => {
    setThemeColors(defaultColors);
    
    // Save to API
    try {
      const config = await loadAppConfig();
      const updatedConfig = {
        ...config,
        theme: defaultColors,
      };
      await saveAppConfig(updatedConfig);
    } catch (error) {
      console.error('Failed to reset theme colors in API:', error);
    }
  };

  // Update theme colors locally without saving to API
  const setThemeColorsLocal = (newColors) => {
    setThemeColors(newColors);
  };

  return (
    <ThemeContext.Provider value={{ theme, themeColors, updateThemeColors, resetThemeColors, setThemeColorsLocal, isLoaded }}>
      {children}
    </ThemeContext.Provider>
  );
};
